import { useEffect, useRef, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useI18N, useCreateList, usePostCreateListEmpty } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import Loader from "../Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { XIcon } from "lucide-react";
import WideButton from "../WideButton";

interface CreateListProps {
  onClose: () => void;
  businessId: number;
  venueDetails: any;
  fullSize?: boolean;
  onCreateList: (listName: string) => void;
}

const CreateList = ({ onClose, businessId, venueDetails, onCreateList }: CreateListProps) => {
  const isDark = useDarkMode();
  const i18n = useI18N();

  const showToast = useCustomToast();

  const [listName, setListName] = useState("");
  const { error, createList, isLoading, result } = useCreateList();
  const {
    error: emptyError,
    createList: createEmptyList,
    isLoading: isEmptyLoading,
    result: emptyResult,
  } = usePostCreateListEmpty();

  useEffect(() => {
    if (result) {
      AnalyticsService.trackGAEventHandler("mySavedPlacesCreatedList");

      AnalyticsService.trackMx("mySavedPlacesCreatedList", {
        ListName: listName,
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });
      showToast(i18n.get("addedToYourSavedPlaces"), "success");
      onClose();
    }
  }, [result]);

  useEffect(() => {
    if (emptyResult) {
      AnalyticsService.trackGAEventHandler("mySavedPlacesCreatedList");

      AnalyticsService.trackMx("mySavedPlacesCreatedList", {
        ListName: listName,
      });

      showToast(i18n.get("addedToYourSavedPlaces"), "success");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyResult]);

  useEffect(() => {
    if (error) {
      console.log("error", error);

      AnalyticsService.trackGAEventHandler("mySavedPlacesCreateListError");
      AnalyticsService.trackMx("mySavedPlacesCreateListError", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark, venueDetails]);

  useEffect(() => {
    if (emptyError) {
      console.log("error", emptyError);

      AnalyticsService.trackGAEventHandler("mySavedPlacesCreateListError");
      AnalyticsService.trackMx("mySavedPlacesCreateListError", {});

      showToast(emptyError.message, "error");
    }
  }, [emptyError]);

  const handleSave = () => {
    console.log("handleSave", businessId);
    if (!businessId) {
      console.log("no businessId", businessId);
      return;
    }

    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    AnalyticsService.trackGAEventHandler("mySavedPlacesCreateListSubmit");
    AnalyticsService.trackMx("mySavedPlacesCreateListSubmit", {
      VenueId: venueDetails.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.types,
    });

    createList({
      name: listName,
      venueId: businessId,
    });

    onCreateList(listName);
  };

  const handleEmptyListCreate = () => {
    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    AnalyticsService.trackGAEventHandler("mySavedPlacesCreateListSubmit");
    AnalyticsService.trackMx("mySavedPlacesCreateListSubmit", {
      ListName: listName,
    });

    createEmptyList({
      name: listName,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div role="dialog" aria-label={i18n.get("createList")} ref={firstFocusRef}  tabIndex={0}>
      {(isLoading || isEmptyLoading) && <Loader />}

      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(50vh-50px)] overflow-y-auto"

      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          >
            {i18n.get("createList")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="w-full md:max-w-[70%] mx-auto mt-5">
            <div className="flex flex-col gap-4 mb-2">
              <FloatingLabelInput
                label={i18n.get("yourListName")}
                required
                type="text"
                value={listName}
                placeholder={i18n.get("enterYourListName")}
                onChange={(event) => {
                  setListName(event.target.value);
                }}
                maxLength={50}
              />
            </div>

            <div className="mb-[10px] mt-[2px] flex-row">
              <div
                id="charCount"
                className="button-small-medium text-neutral-500 mt-3"
                tabIndex={0}
              >
                <span className={listName.length < 5 ? "text-red-600" : ""}>
                  {listName.length}
                </span>
                /50 Characters - at least 5 characters required
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-neutral-300 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex flex-col justify-center items-center w-full py-6 md:py-3  md:max-w-[60%]">
            <WideButton
              className="grow"
              disabled={listName.length < 3}
              onClick={() => {
                if (businessId) {
                  handleSave();
                } else {
                  handleEmptyListCreate();
                }
              }}
            >
              {i18n.get("create")}
            </WideButton>
          </div>
        </footer>
        <button
          onClick={onClose}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </div>
  );
};

export default CreateList;
